// src/components/pdf/HeaderPage.jsx
import React from 'react';
import { Page, Text, View } from '@react-pdf/renderer';
import styles from './styles';
import { getClassementLabel, getPaysLabel, getSurfaceLabel, getCategorieLabel, getTourLabel, getCategorieAgeLabel, getEpreuveLabel } from './utils/refUtils';
import { getPlayerDisplayName, getPlayerFullName } from '../../playerDisplay';

const HeaderPage = ({ match }) => {
  // Déterminer le vainqueur à partir de currentScore.sets
  const determineWinner = () => {
    const sets = match.currentScore?.sets || [];
    if (sets.length === 0) return getPlayerFullName(match, 'J1');
    
    const j1Sets = sets.filter(set => set[0] > set[1]).length;
    const j2Sets = sets.filter(set => set[1] > set[0]).length;
    
    return j1Sets > j2Sets ? getPlayerFullName(match, 'J1') : getPlayerFullName(match, 'J2');
  };

  // FIX 2: Formater le score (INVERSÉ car vision J1)
  const formatScore = () => {
    const sets = match.currentScore?.sets || [];
    if (sets.length === 0) return '-';
    
    // IMPORTANT: Le score est stocké du point de vue J1
    // Donc set[0] = score J1, set[1] = score J2
    // On inverse pour afficher J2-J1 si J2 a gagné
    const winner = determineWinner();
    const j2Name = getPlayerFullName(match, 'J2');
    
    if (winner === j2Name) {
      // J2 a gagné, on inverse le score pour l'afficher J2-J1
      return sets.map(set => `${set[1]}-${set[0]}`).join(' ');
    }
    
    // J1 a gagné, score normal
    return sets.map(set => `${set[0]}-${set[1]}`).join(' ');
  };

  // Formater la date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <Page size="A4" style={styles.page}>
      {/* Titre principal */}
      <Text style={styles.mainTitle}>Rapport Ten Stats</Text>

      {/* Résumé du match */}
      <View style={styles.matchInfo}>
        <Text style={[styles.text, { fontSize: 12, marginBottom: 8 }]}>
          Match entre {getPlayerFullName(match, 'J1')} & {getPlayerFullName(match, 'J2')}
        </Text>
        
        {/* FIX 1: Vainqueur + Score sur la même ligne */}
        <Text style={[styles.boldText, { fontSize: 14, color: '#4CAF50' }]}>
          Vainqueur {determineWinner()} : {formatScore()}
        </Text>
      </View>

      {/* Informations des joueurs */}
      <Text style={styles.sectionTitle}>Joueurs</Text>
      <View style={styles.twoColumns}>
        {/* Joueur 1 */}
        <View style={styles.playerCard}>
          {/* FIX 3: Carré couleur + Nom */}
          <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8, justifyContent: 'center' }}>
            <View style={{
              width: 12,
              height: 12,
              backgroundColor: '#1976D2',
              marginRight: 6,
              borderRadius: 2
            }} />
            <Text style={[styles.boldText, { fontSize: 14 }]}>
              {getPlayerDisplayName(match, 'J1')}
            </Text>
          </View>
          
          <View style={styles.statRow}>
            <Text style={styles.label}>Club :</Text>
            <Text style={styles.value}>{match.joueur1Club || '-'}</Text>
          </View>
          <View style={styles.statRow}>
            <Text style={styles.label}>Classement :</Text>
            <Text style={styles.value}>{getClassementLabel(match.joueur1ClassementCode)}</Text>
          </View>
          <View style={styles.statRow}>
            <Text style={styles.label}>Pays :</Text>
            <Text style={styles.value}>{getPaysLabel(match.joueur1PaysCode)}</Text>
          </View>
          <View style={styles.statRow}>
            <Text style={styles.label}>Main :</Text>
            <Text style={styles.value}>
              {match.joueur1Droitier === true ? 'Droitier' : match.joueur1Droitier === false ? 'Gaucher' : '-'}
            </Text>
          </View>
        </View>

        {/* Joueur 2 */}
        <View style={styles.playerCard}>
          {/* FIX 3: Carré couleur + Nom */}
          <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8, justifyContent: 'center' }}>
            <View style={{
              width: 12,
              height: 12,
              backgroundColor: '#FF9800',
              marginRight: 6,
              borderRadius: 2
            }} />
            <Text style={[styles.boldText, { fontSize: 14 }]}>
              {getPlayerDisplayName(match, 'J2')}
            </Text>
          </View>
          
          <View style={styles.statRow}>
            <Text style={styles.label}>Club :</Text>
            <Text style={styles.value}>{match.joueur2Club || '-'}</Text>
          </View>
          <View style={styles.statRow}>
            <Text style={styles.label}>Classement :</Text>
            <Text style={styles.value}>{getClassementLabel(match.joueur2ClassementCode)}</Text>
          </View>
          <View style={styles.statRow}>
            <Text style={styles.label}>Pays :</Text>
            <Text style={styles.value}>{getPaysLabel(match.joueur2PaysCode)}</Text>
          </View>
          <View style={styles.statRow}>
            <Text style={styles.label}>Main :</Text>
            <Text style={styles.value}>
              {match.joueur2Droitier === true ? 'Droitier' : match.joueur2Droitier === false ? 'Gaucher' : '-'}
            </Text>
          </View>
        </View>
      </View>

      {/* Caractéristiques du match */}
      <Text style={styles.sectionTitle}>Caractéristiques du match</Text>
      <View style={styles.infoCard}>
        <View style={styles.statRowCompact}>
          <Text style={styles.label}>Date :</Text>
          <Text style={styles.value}>{formatDate(match.dateMatch)}</Text>
        </View>
        <View style={styles.statRowCompact}>
          <Text style={styles.label}>Tournoi :</Text>
          <Text style={styles.value}>{match.tournoiNom || '-'}</Text>
        </View>
        <View style={styles.statRowCompact}>
          <Text style={styles.label}>Surface :</Text>
          <Text style={styles.value}>{getSurfaceLabel(match.surfaceCode)}</Text>
        </View>
        <View style={styles.statRowCompact}>
          <Text style={styles.label}>Catégorie tournoi :</Text>
          <Text style={styles.value}>{getCategorieLabel(match.categorieCode)}</Text>
        </View>
        <View style={styles.statRowCompact}>
          <Text style={styles.label}>Tour :</Text>
          <Text style={styles.value}>{getTourLabel(match.tourCode)}</Text>
        </View>
        <View style={styles.statRowCompact}>
          <Text style={styles.label}>Catégorie âge :</Text>
          <Text style={styles.value}>{getCategorieAgeLabel(match.categorieageCode)}</Text>
        </View>
        <View style={styles.statRowCompact}>
          <Text style={styles.label}>Épreuve :</Text>
          <Text style={styles.value}>{getEpreuveLabel(match.epreuveCode)}</Text>
        </View>
        <View style={styles.statRowCompact}>
          <Text style={styles.label}>Format :</Text>
          <Text style={styles.value}>
            {match.formatMatch === 'BEST_OF_3' ? '2 sets gagnants' : '3 sets gagnants'}
          </Text>
        </View>
        <View style={styles.statRowCompact}>
          <Text style={styles.label}>Vainqueur Toss :</Text>
          <Text style={styles.value}>
            {match.toss === 'J1' ? getPlayerDisplayName(match, 'J1') : getPlayerDisplayName(match, 'J2')}
          </Text>
        </View>
        <View style={styles.statRowCompact}>
          <Text style={styles.label}>1er Serveur :</Text>
          <Text style={styles.value}>
            {match.premierServeur === 'J1' ? getPlayerDisplayName(match, 'J1') : getPlayerDisplayName(match, 'J2')}
          </Text>
        </View>
      </View>

      {/* 🆕 Commentaire général du match */}
      {match.matchComments && (
        <>
          <Text style={[styles.sectionTitle, { marginTop: 12 }]}>Commentaires du match</Text>
          <View style={styles.infoCard}>
            <Text style={[styles.text, { fontSize: 10, lineHeight: 1.4 }]}>
              {match.matchComments}
            </Text>
          </View>
        </>
      )}
    </Page>
  );
};

export default HeaderPage;